// Time zones (time-zone PR). Times always come from the server in UTC; this module is
// the one place that decides which zone they are shown in: the zone the person (or an
// admin) picked in Settings, or this device's zone when it is "Automatic". Every time
// the app shows goes through here, so "Today" / "Yesterday" also follow the picked zone.
// Pure apart from the picked zone; tested in test/time.test.ts.

const DAY = 86_400_000;

let picked: string | null = null;
let pickedUnknown = false;

/** This device's own zone ("America/New_York"); UTC if the browser can't say. */
export function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** True if this browser can show times in that zone. */
export function knownZone(z: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: z });
    return true;
  } catch {
    return false;
  }
}

/**
 * The zone saved on the account (null = Automatic). A zone this browser doesn't know
 * (a very old browser) falls back to the device's zone; Settings says so.
 */
export function setPickedZone(z: string | null | undefined): void {
  if (!z) {
    picked = null;
    pickedUnknown = false;
  } else if (knownZone(z)) {
    picked = z;
    pickedUnknown = false;
  } else {
    picked = null;
    pickedUnknown = true;
  }
}

/** The zone the account picked, if this browser knows it; null = Automatic. */
export const pickedZone = (): string | null => picked;
/** The account's zone is unknown to this browser, so times use the device's zone. */
export const pickedZoneUnknown = (): boolean => pickedUnknown;
/** The zone every time on screen is shown in. */
export const shownZone = (): string => picked ?? deviceZone();

// ── formatters (cached: building one is slow and the history list makes many) ──

const cache = new Map<string, Intl.DateTimeFormat>();
function fmt(locale: string | undefined, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale ?? ''}|${JSON.stringify(opts)}`;
  let f = cache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts);
    cache.set(key, f);
  }
  return f;
}

const toMs = (t: string | number | Date): number => (typeof t === 'number' ? t : typeof t === 'string' ? Date.parse(t) : t.getTime());

/** Year / month / day (and the time of day) of an instant in a zone. */
function partsIn(t: number, zone: string): { y: number; m: number; d: number; h: number; mi: number; s: number } {
  const out = { y: 0, m: 0, d: 0, h: 0, mi: 0, s: 0 };
  const f = fmt('en-US', { timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' });
  for (const p of f.formatToParts(t)) {
    const n = Number(p.value);
    if (p.type === 'year') out.y = n;
    else if (p.type === 'month') out.m = n;
    else if (p.type === 'day') out.d = n;
    else if (p.type === 'hour') out.h = n % 24;
    else if (p.type === 'minute') out.mi = n;
    else if (p.type === 'second') out.s = n;
  }
  return out;
}

/** The calendar day of an instant in a zone, as a day number (for "Today" / "Yesterday"). */
export function dayNumber(t: number, zone: string = shownZone()): number {
  const p = partsIn(t, zone);
  return Math.round(Date.UTC(p.y, p.m - 1, p.d) / DAY);
}

/** Calendar days from `t` to `now` in a zone: 0 = same day, 1 = yesterday. */
export function daysAgo(t: number, now: number, zone: string = shownZone()): number {
  return dayNumber(now, zone) - dayNumber(t, zone);
}

const yearOf = (t: number, zone: string) => partsIn(t, zone).y;

/** Minutes the zone is ahead of UTC at that moment (Karachi +300, New York −240 in summer). */
export function offsetMinutes(zone: string, at: number = Date.now()): number {
  const p = partsIn(at, zone);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s);
  return Math.round((asUtc - Math.floor(at / 1000) * 1000) / 60_000);
}

/** "UTC+05:00", "UTC−08:00" (a real minus sign), "UTC+05:45". */
export function offsetText(min: number): string {
  const a = Math.abs(min);
  return `UTC${min < 0 ? '−' : '+'}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
}

// ── zone names ──

/** Zones pinned at the top of the picker, with their everyday names. */
export const US_ZONES: ReadonlyArray<{ id: string; title: string; city: string }> = [
  { id: 'America/New_York', title: 'Eastern Time', city: 'New York' },
  { id: 'America/Chicago', title: 'Central Time', city: 'Chicago' },
  { id: 'America/Denver', title: 'Mountain Time', city: 'Denver' },
  { id: 'America/Los_Angeles', title: 'Pacific Time', city: 'Los Angeles' },
  { id: 'America/Anchorage', title: 'Alaska Time', city: 'Anchorage' },
  { id: 'Pacific/Honolulu', title: 'Hawaii Time', city: 'Honolulu' },
];

// Old city spellings some browsers still list (Chrome lists "Asia/Calcutta").
const CITY_FIX: Record<string, string> = {
  Calcutta: 'Kolkata',
  Katmandu: 'Kathmandu',
  Saigon: 'Ho Chi Minh City',
  Kiev: 'Kyiv',
  Rangoon: 'Yangon',
  Godthab: 'Nuuk',
};

const isUtc = (z: string) => z === 'UTC' || z === 'Etc/UTC' || z === 'Etc/GMT';

/** "Asia/Karachi" → "Karachi", "America/Argentina/Buenos_Aires" → "Buenos Aires". */
export function zoneCity(z: string): string {
  if (isUtc(z)) return 'UTC';
  const last = (z.split('/').pop() ?? z).replace(/_/g, ' ');
  return CITY_FIX[last] ?? last;
}

/** How a zone is named on screen: "Pacific Time", "Karachi", "UTC". */
export function zoneTitle(z: string): string {
  return US_ZONES.find((u) => u.id === z)?.title ?? zoneCity(z);
}

/** "Pakistan Standard Time", "Eastern Time" (the browser's long name; '' if it has only an offset). */
export function zoneLongName(z: string, at: number = Date.now()): string {
  try {
    const v = fmt('en-US', { timeZone: z, timeZoneName: 'longGeneric' }).formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? '';
    return /^GMT[+-−]/.test(v) ? '' : v;
  } catch {
    return '';
  }
}

/**
 * The label after a time: "EDT", "PST", "GMT", "UTC" — or "Karachi time" where the
 * browser has no short name and would say "GMT+5" (§11.2 item 59).
 */
export function zoneShort(z: string, at: number = Date.now()): string {
  if (isUtc(z)) return 'UTC';
  let v = '';
  try {
    v = fmt('en-US', { timeZone: z, timeZoneName: 'short' }).formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    /* unknown zone: use the city */
  }
  return !v || /^(GMT|UTC)[+-−]/.test(v) ? `${zoneTitle(z)} time` : v;
}

/** "Karachi (UTC+05:00)", "Pacific Time (UTC−07:00)": the zone with its offset now. */
export function zoneWithOffset(z: string, at: number = Date.now()): string {
  return `${zoneTitle(z)} (${offsetText(offsetMinutes(z, at))})`;
}

// ── times on screen ──

/** "9:41 AM" (the phone's lists follow the browser's language). */
export function clockTime(t: string | number | Date, locale?: string, zone: string = shownZone()): string {
  return fmt(locale, { timeZone: zone, hour: 'numeric', minute: '2-digit' }).format(toMs(t));
}

/** History / voicemail day headings: "Today", "Yesterday", "Wednesday, 28 October", "3 October", "3 October 2025". */
export function listDayLabel(t: string | number | Date, now: number = Date.now(), zone: string = shownZone()): string {
  const ms = toMs(t);
  const diff = daysAgo(ms, now, zone);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return fmt(undefined, {
    timeZone: zone,
    ...(diff < 7 && diff > 0 ? { weekday: 'long' } : {}),
    day: 'numeric',
    month: 'long',
    ...(yearOf(ms, zone) === yearOf(now, zone) ? {} : { year: 'numeric' }),
  }).format(ms);
}

/** "Oct 3" this year, "Oct 3, 2025" otherwise. */
export function shortDate(t: string | number | Date, now: number = Date.now(), zone: string = shownZone()): string {
  const ms = toMs(t);
  const sameYear = yearOf(ms, zone) === yearOf(now, zone);
  return fmt('en-US', { timeZone: zone, month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) }).format(ms);
}

/** "3 Oct 2026" (Settings → two-step "set up on"; the browser's language). */
export function longDate(t: string | number | Date, zone: string = shownZone()): string {
  return fmt(undefined, { timeZone: zone, day: 'numeric', month: 'short', year: 'numeric' }).format(toMs(t));
}

/** "Oct 28, 4:30 AM EDT": a moment with its zone label (links, temporary passwords, timeline lines). */
export function momentText(t: string | number | Date, now: number = Date.now(), zone: string = shownZone()): string {
  const ms = toMs(t);
  const sameYear = yearOf(ms, zone) === yearOf(now, zone);
  const body = fmt('en-US', { timeZone: zone, month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }), hour: 'numeric', minute: '2-digit' }).format(ms);
  return `${body} ${zoneShort(zone, ms)}`;
}

/** The last millisecond of a calendar day ("2026-10-31") in a zone, as an ISO time in UTC. */
export function endOfDayIso(date: string, zone: string = shownZone()): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new Error(`Not a date: ${date}`);
  // midnight that starts the next day there: guess with today's offset, then correct once
  // (the offset can differ at midnight on a daylight-saving day)
  const nextUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1);
  let t = nextUtc - offsetMinutes(zone, nextUtc) * 60_000;
  t = nextUtc - offsetMinutes(zone, t) * 60_000;
  return new Date(t - 1).toISOString();
}

/** "2026-10-31": today's date in a zone (the timeline's date picker). */
export function todayIn(zone: string = shownZone(), now: number = Date.now()): string {
  const p = partsIn(now, zone);
  return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

/**
 * The line over History / Voicemail when the picked zone isn't the device's:
 * "Times shown in Pacific Time". null when they show the same clock.
 */
export function shownZoneNote(now: number = Date.now()): string | null {
  if (!picked) return null;
  return offsetMinutes(picked, now) === offsetMinutes(deviceZone(), now) ? null : `Times shown in ${zoneTitle(picked)}`;
}

// ── the picker's list ──

export interface ZoneOption {
  id: string;
  /** "Eastern Time" for the pinned US zones, the city for the rest */
  title: string;
  /** second line: "New York", "Asia · Pakistan Standard Time" */
  sub: string;
  offset: number;
  offsetText: string;
  /** lower-case words the search matches */
  keys: string;
}

function regionOf(z: string): string {
  const parts = z.split('/');
  return parts.length > 2 ? `${parts[0]} · ${parts[1]!.replace(/_/g, ' ')}` : (parts[0] ?? '');
}

function optionOf(z: string, now: number, pinned?: { title: string; city: string }): ZoneOption {
  const offset = offsetMinutes(z, now);
  const long = zoneLongName(z, now);
  const year = new Date(now).getUTCFullYear();
  const shorts = new Set([zoneShort(z, Date.UTC(year, 0, 15)), zoneShort(z, Date.UTC(year, 6, 15))]);
  const sign = offset < 0 ? '-' : '+';
  const a = Math.abs(offset);
  const h = Math.floor(a / 60);
  const mi = a % 60;
  const offs = [`${sign}${h}`, `${sign}${String(h).padStart(2, '0')}`, `${sign}${h}:${String(mi).padStart(2, '0')}`, `${sign}${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`];
  const keys = `| ${[z, z.replace(/_/g, ' '), zoneCity(z), long, regionOf(z), ...shorts, ...offs.flatMap((o) => [o, `utc${o}`, `gmt${o}`]), pinned?.title ?? '', pinned?.city ?? '']
    .join(' | ')
    .toLowerCase()} |`;
  return {
    id: z,
    title: pinned?.title ?? zoneCity(z),
    sub: pinned ? pinned.city : [regionOf(z), long].filter(Boolean).join(' · '),
    offset,
    offsetText: offsetText(offset),
    keys,
  };
}

/**
 * Every zone this browser knows: the six US zones first, then the world sorted by
 * offset and name. Raw "Etc/GMT+5" zones are left out (their sign is backwards); UTC stays.
 */
export function zoneOptions(now: number = Date.now()): { pinned: ZoneOption[]; all: ZoneOption[] } {
  let ids: string[] = [];
  try {
    ids = Intl.supportedValuesOf('timeZone');
  } catch {
    /* very old browser: the US zones, this device's zone and UTC */
  }
  const set = new Set(ids.filter((z) => z.includes('/') && !z.startsWith('Etc/')));
  set.add('UTC');
  const dev = deviceZone();
  if (knownZone(dev)) set.add(dev);
  const pinnedIds = new Set(US_ZONES.map((u) => u.id));
  const pinned = US_ZONES.filter((u) => knownZone(u.id)).map((u) => optionOf(u.id, now, u));
  const all = [...set]
    .filter((z) => !pinnedIds.has(z) && knownZone(z))
    .map((z) => optionOf(z, now))
    .sort((a, b) => a.offset - b.offset || a.title.localeCompare(b.title));
  return { pinned, all };
}

/** The options matching what was typed ("kar", "+5", "pst", "new york"); every word must match. */
export function searchZones(options: ZoneOption[], query: string): ZoneOption[] {
  const words = query
    .toLowerCase()
    .replace(/−/g, '-')
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return options;
  // an offset ("+5", "utc-8", "+05:30") must match a whole offset, so "+1" doesn't find +10 / +11
  const offsetWord = /^(utc|gmt)?[+-]\d/;
  const hits = options.filter((o) => words.every((w) => (offsetWord.test(w) ? o.keys.includes(`| ${w} |`) : o.keys.includes(w))));
  // names that start with what was typed first ("kar" → Karachi before Dakar); otherwise keep the offset order
  const q = words.join(' ');
  const rank = (o: ZoneOption) => {
    const t = o.title.toLowerCase();
    return t.startsWith(q) ? 0 : t.includes(q) ? 1 : 2;
  };
  return hits.map((o, i) => ({ o, i, r: rank(o) })).sort((a, b) => a.r - b.r || a.i - b.i).map((x) => x.o);
}
